import { ReferrerType } from '@enums/visitor-identity';

const TRUSTED_DOMAIN_PATTERNS = ['localhost', '.vestiairecollective.com'];
const HEADLESS_INDICATORS = ['Headless', 'PhantomJS', 'Selenium', 'Puppeteer'];

export const isTrustedDomain = (hostname: string): boolean => {
  const isLocalhost = hostname === 'localhost';
  const isVestiaireDomain = TRUSTED_DOMAIN_PATTERNS.some(
    (pattern) => hostname === pattern || hostname.endsWith(pattern),
  );
  return process.env.NODE_ENV === 'development' ? isLocalhost : isVestiaireDomain;
};

export type VisitorIdentityFromRequest = {
  referrer: string;
  isVestiaireReferrer: boolean;
};

export const getReferrerType = (referrer: string): { referrerType: ReferrerType; isTrustedReferrer: boolean } => {
  if (!referrer) {
    return { referrerType: ReferrerType.Direct, isTrustedReferrer: false };
  }

  try {
    const refUrl = new URL(referrer);
    const isTrusted = TRUSTED_DOMAIN_PATTERNS.some(
      (pattern) => refUrl.hostname === pattern || refUrl.hostname.endsWith(pattern),
    );
    return {
      referrerType: isTrusted ? ReferrerType.Internal : ReferrerType.External,
      isTrustedReferrer: isTrusted,
    };
  } catch {
    return { referrerType: ReferrerType.External, isTrustedReferrer: false };
  }
};

export const getVisitorIdentityFromRequest = (req?: { headers?: { referer?: string } }): VisitorIdentityFromRequest => {
  const referrer = req?.headers?.referer?.toString() ?? '';
  const { isTrustedReferrer: isVestiaireReferrer } = getReferrerType(referrer);
  return { referrer, isVestiaireReferrer };
};

export const getLowerCaseSearchParams = (params: URLSearchParams): URLSearchParams => {
  const lower = new URLSearchParams();
  for (const [name, value] of params.entries()) {
    lower.append(name.toLowerCase(), value);
  }
  return lower;
};

export const isLikelyScraper = ({
  userId,
  anonymousId,
  referrer,
  userAgent,
  hasUtmOrSem,
  acceptLanguage,
  acceptEncoding,
}: {
  userId?: string;
  anonymousId?: string;
  referrer?: string;
  userAgent: string;
  hasUtmOrSem: boolean;
  acceptLanguage?: string | null;
  acceptEncoding?: string | null;
}): boolean => {
  const looksAnonymous = !userId && !anonymousId;
  const noMarketingSignals = !hasUtmOrSem && !Boolean(referrer);
  const hasHeadlessIndicator = HEADLESS_INDICATORS.some((s) => userAgent.includes(s));
  const looksSuspiciousUA = !userAgent.includes('Mozilla') || hasHeadlessIndicator;
  const missingStandardHeaders = !acceptLanguage || !acceptEncoding;

  return looksAnonymous && noMarketingSignals && (looksSuspiciousUA || missingStandardHeaders);
};
